import type { ObjectFacet } from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { DeliveryMethod, FacetKey } from '@shared/gql/document-nodes'
import { Alert, AlertType } from '@sok/design-system'
import { GeneralError } from 'components/errors/ErrorPage'
import { CartSidebarLayout } from 'components/layout/CartSidebarLayout'
import { ProductList } from 'components/ProductList'
import { NextSeo } from 'next-seo'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useProductSearch } from 'services/Search/hooks/use-product-search'
import { useQueryStringParam } from 'services/Search/hooks/use-query-string-param'
import { getFilterParams, isObjectFacet, SUPPORTED_FACETS } from 'services/Search/utils/filters'
import { SORT_OPTIONS_SEARCH_RESULTS } from 'services/Search/utils/sort'
import styled, { useTheme } from 'styled-components'
import { useAlcoholAllowedForDeliveryArea } from 'utils/hooks/delivery/use-alcohol-allowed'
import { useDeliveryDate } from 'utils/hooks/delivery/use-delivery-date'
import { useStoreId } from 'utils/hooks/store/use-store-id'
import { useCanonicalUrl } from 'utils/hooks/use-canonical-url'
import { usePrevious } from 'utils/hooks/use-previous'
import { useURLSearchParams } from 'utils/hooks/use-url-search-params'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { pushData, trackCustomEvent } from 'utils/tracking/custom-events/custom-events'
import { mapGa4Availabilities } from 'utils/tracking/event-product-mapper'
import {
  EventAction,
  EventCategory,
  TrackingContext,
} from 'utils/tracking/interfaces/data-layer-events'
import { trackPageview } from 'utils/tracking/pageview'
import { GA4_trackImpressions } from 'utils/tracking/track-product-impressions'
import { useGetInitialDeliverySelections } from 'views/delivery-modal/method-and-store/use-get-initial-delivery-selections'
import { CategoryLinks } from 'views/products/CategoryLinks'

import { Filters } from './Filters'
import { SearchResultsTitle } from './SearchResultsTitle'
import { Sort } from './Sort'

const StyledGeneralError = styled(GeneralError)`
  height: auto;
  padding: ${({ theme }) => theme.spacings.xxxLarge} 0;
`

export const SearchResultsPage: FC = () => {
  const { t } = useTranslation()
  const { selectedStoreId } = useStoreId()
  const alcoholSellingAllowed = useAlcoholAllowedForDeliveryArea()
  const { initialDeliveryMethod } = useGetInitialDeliverySelections()
  const isHomeDelivery = initialDeliveryMethod === DeliveryMethod.HomeDelivery
  const theme = useTheme()
  const seoCanonical = useCanonicalUrl()

  const {
    data: searchData,
    facets,
    loading: searchLoading,
    error,
    hasMore,
    loadingMore,
    loadMore,
    variables,
  } = useProductSearch({
    context: TrackingContext.SEARCH_RESULTS,
    /** Search should be disabled while loading alcohol status to prevent double-search */
    disabled: alcoholSellingAllowed === null,
    facets: SUPPORTED_FACETS,
    /** Display alcohol by default if store is not selected */
    includeAgeLimitedByAlcohol: selectedStoreId ? !!alcoholSellingAllowed : true,
  })

  const queryStringFromUrl = useQueryStringParam()
  const queryString = variables?.queryString ?? queryStringFromUrl
  const previousQuery = usePrevious(queryString)

  const searchResults = searchData?.store?.products?.items
  const searchTotal =
    searchData?.store?.products?.total !== undefined
      ? Number(searchData?.store?.products?.total)
      : undefined
  const previousTotal = usePrevious(searchTotal)

  useEffect((): void => {
    const totalChanged = previousTotal !== searchTotal

    if (!totalChanged || searchTotal === undefined) {
      return
    }

    trackPageview({
      path: '/hakutulokset', // works until localized routes
      query: `?queryString=${queryString}`,
    })

    if (searchTotal) {
      const trackingContext = `${TrackingContext.SEARCH_RESULTS}: ${queryString}`
      pushData({
        searchResultsCount: searchTotal,
        viewedCategory: {
          slug: trackingContext,
          id: trackingContext,
        },
      })
    }

    if (searchTotal === 0) {
      trackCustomEvent({
        category: EventCategory.SEARCH_RESULTS,
        action: EventAction.NO_RESULTS,
        label: queryString,
      })
    }
  }, [previousQuery, previousTotal, queryString, searchTotal])

  // Index to track which products should be reported as impressions,
  // so that lazy-loaded results are not reported twice.
  const impressionSlice = useRef(0)

  // Reset index when search is updated and not only loading more results
  useEffect(() => {
    if (searchLoading && !loadingMore) {
      impressionSlice.current = 0
    }
  }, [loadingMore, searchLoading])

  const availabilityDate = useDeliveryDate()

  // Report a single slice of Product Impressions to Analytics,
  // keeping track of lazy-loaded results.
  const listName = `${TrackingContext.SEARCH_RESULTS}: ${queryString}`
  useEffect(() => {
    const productArray = searchResults || []
    const start = impressionSlice.current
    const slicedDifference = productArray.slice(start)

    if (slicedDifference.length) {
      GA4_trackImpressions({
        listName,
        trackingContext: TrackingContext.SEARCH_RESULTS,
        products: slicedDifference.map(mapGa4Availabilities(availabilityDate)),
        listPosition: start,
      })
    }

    // Increment slice
    impressionSlice.current += slicedDifference.length
  }, [availabilityDate, impressionSlice, listName, queryString, searchResults])

  const showLoadingIndicators = (searchLoading || searchTotal === undefined) && !error

  const titleText = showLoadingIndicators
    ? t(queryString ? 'Searching for term...' : 'Searching...', { term: queryString })
    : t(queryString ? 'Search result for term count' : 'Search result count', {
        count: searchTotal,
        term: queryString,
      })

  const seoTitle = queryString
    ? `${t('Search results for term', { term: queryString })} | ${theme.seoTags.title}`
    : `${t('Search results')} | ${theme.seoTags.title}`

  const resultsIncludeAlcohol = searchResults?.some((p) => p?.isAgeLimitedByAlcohol) || false

  const handleInfiniteScroll = useCallback(async () => {
    if (hasMore) await loadMore()
  }, [hasMore, loadMore])

  const categoryFacetList = useMemo(
    () =>
      facets
        ?.find((f): f is ObjectFacet => isObjectFacet(f) && f.key === FacetKey.Category)
        ?.objectValue?.map((v) => ({ name: v.name, slug: v.value, count: v.doc_count })) ||
      undefined,
    [facets],
  )

  const { params } = useURLSearchParams()
  const searchParamString = useMemo(() => {
    const search = getFilterParams(params).toString()
    return search ? `?${search}` : ''
  }, [params])

  const searchProvider = variables?.searchProvider

  return (
    <TrackingContextProvider
      searchProvider={searchProvider}
      listName={listName}
      trackingContext={TrackingContext.SEARCH_RESULTS}
    >
      <CartSidebarLayout data-test-id="product-results" showFooter={!hasMore}>
        <NextSeo title={seoTitle} noindex canonical={seoCanonical} />

        {isHomeDelivery && resultsIncludeAlcohol && (
          <Alert
            message={t('Alcohol items only available for pickup')}
            type={AlertType.INFO}
            closable
          />
        )}

        {selectedStoreId && !alcoholSellingAllowed && !isHomeDelivery && resultsIncludeAlcohol && (
          <Alert
            message={t('Alcohol items not available in this store')}
            type={AlertType.INFO}
            closable
          />
        )}

        {!error && (
          <SearchResultsTitle
            actions={
              <>
                <Filters loading={showLoadingIndicators} facets={facets} />
                <Sort sortOptions={SORT_OPTIONS_SEARCH_RESULTS} />
              </>
            }
            titleText={titleText}
          />
        )}

        {!error && categoryFacetList && (
          <CategoryLinks items={categoryFacetList} searchParamString={searchParamString} />
        )}

        {error ? (
          <StyledGeneralError />
        ) : (
          <ProductList
            hasMore={hasMore}
            loading={showLoadingIndicators}
            loadingMore={loadingMore}
            loadMore={handleInfiniteScroll}
            products={searchResults}
          />
        )}
      </CartSidebarLayout>
    </TrackingContextProvider>
  )
}
